import React, { useState } from "react";
import { X, Loader2, ImageIcon, Trash2 } from "lucide-react";
import { Label } from "../ui/Label";
import { Input } from "../ui/Input";
import { Textarea } from "../ui/Textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/Select";
import { Switch } from "../ui/Switch";
import { Button } from "../ui/Button";
import { ImageWithFallback } from "../figma/ImageWithFallback";
import { getImageUrl, decodeHtmlEntities } from "../../utils";
import { categoryService } from "../../services/categories";
import { influencerService } from "../../services/influencers";
import { toast } from "sonner";

interface AddCategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (data: any) => void;
  categories: any[];
  isSubcategory?: boolean;
  initialParentId?: string;
}

export function AddCategoryModal({ isOpen, onClose, onSuccess, categories, isSubcategory, initialParentId }: AddCategoryModalProps) {
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    parent_id: initialParentId || "",
    image: "",
    active: true
  });

  if (!isOpen) return null;

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploadingImage(true);
      const data = await influencerService.uploadImage(file, 'categories');
      setFormData({ ...formData, image: data.path ?? data.location ?? data.file_name ?? "" });
      toast.success("Imagem enviada com sucesso");
    } catch (error) {
      toast.error("Erro ao enviar imagem");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name) {
      toast.error("O nome é obrigatório");
      return;
    }

    try {
      setLoading(true);
      const dataToSave = {
        ...formData,
        parent_id: isSubcategory ? parseInt(formData.parent_id) || null : null
      };
      const response = await categoryService.create(dataToSave);
      toast.success(`${isSubcategory ? 'Subcategoria' : 'Categoria'} salva com sucesso!`);
      onSuccess(response.data || response);
      onClose();
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Erro ao salvar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-[100] bg-black/20 backdrop-blur-md">
      <div className="bg-white rounded-lg p-6 w-full max-w-md relative border border-gray-200 shadow-xl max-h-[90vh] overflow-y-auto text-black">
        <button onClick={onClose} className="absolute top-3 right-3 text-white bg-[#D4C070] hover:bg-[#D4C070] rounded-full p-1 z-10">
          <X className="h-5 w-5" />
        </button>

        <h3 className="text-lg font-semibold mb-4 text-gray-900">
          Adicionar {isSubcategory ? 'Subcategoria' : 'Categoria'}
        </h3>

        <div className="space-y-4">
          {isSubcategory && (
            <div>
              <Label className="block text-sm font-medium text-gray-700 mb-1">Categoria Pai</Label>
              <Select value={formData.parent_id} onValueChange={(val) => setFormData({ ...formData, parent_id: val })}>
                <SelectTrigger className="w-full border-gray-300">
                  <SelectValue placeholder="Selecione uma categoria" />
                </SelectTrigger>
                <SelectContent>
                  {categories.filter(c => !c.parent_id || c.parent_id == 0).map((cat) => (
                    <SelectItem key={cat.id} value={cat.id.toString()}>
                      {decodeHtmlEntities(cat.name)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label className="block text-sm font-medium text-gray-700 mb-2">Imagem</Label>
            <div className="flex items-start gap-4">
              <div className="w-24 h-24 bg-gray-100 rounded-lg overflow-hidden border-2 border-dashed border-gray-300 flex items-center justify-center relative group">
                {formData.image ? (
                  <>
                    <ImageWithFallback src={getImageUrl(formData.image)} alt="Preview" className="w-full h-full object-cover" />
                    <button onClick={() => setFormData({ ...formData, image: "" })} className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white">
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </>
                ) : (
                  <div className="text-center p-2">
                    {uploadingImage ? <Loader2 className="h-6 w-6 animate-spin text-[#D4C070] mx-auto" /> : <ImageIcon className="h-6 w-6 text-gray-400 mx-auto" />}
                  </div>
                )}
              </div>
              <Input type="file" accept="image/*" onChange={handleImageUpload} className="text-xs border-gray-300 flex-1" />
            </div>
          </div>

          <div>
            <Label className="block text-sm font-medium text-gray-700">Nome</Label>
            <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Ex: Sapatos" className="border-gray-300 mt-1" />
          </div>

          <div>
            <Label className="block text-sm font-medium text-gray-700">Descrição</Label>
            <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Descreva brevemente..." rows={3} className="border-gray-300 mt-1" />
          </div>

          <div className="flex items-center justify-between py-2">
            <Label className="text-sm font-medium text-gray-700">Status Ativo</Label>
            <Switch checked={formData.active} onCheckedChange={(checked) => setFormData({ ...formData, active: checked })} />
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={loading}>Cancelar</Button>
          <Button onClick={handleSave} className="bg-[#D4C070] hover:bg-[#849D70] text-white" disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}
          </Button>
        </div>
      </div>
    </div>
  );
}
